// Explore's analysis of a scope: the page reads the scope's traces as it lists
// them (who ran each, with what agent and model, for how long, at what cost),
// and what each did (a few of its prompts, its tools, the sensitive files it
// touched, what review found), folds them into a pack of facts, and a model
// reviews the scope from those facts alone: what is happening in it and which
// traces deserve a look, as plain prose whose names are filters and whose
// traces are links. A follow-up question may also query the scope itself,
// through the analytics tools held to its traces.
//
// The answer's contract, which splitAnalysis reads while it streams:
//   - a first paragraph: what is happening in the scope, and who is doing it;
//   - short paragraphs, each on a trace worth a look, where
//     [Name](filter:<clause>) is a person, group, agent or model to filter by
//     and [words](trace:<ref>) a trace (resolveTraceRefs makes a ref its id);
//   - optionally one ```filters block: up to three lines of `label | clause`.
// A follow-up's answer may also open with a ```breakdown block (JSON).
import { createAnalyticsQueryTool, createExpandPreviewTool, analyticsSchemaTool } from "./analyticsTool.js";
import { PROVIDER_TAG_TO_IDENTITY } from "./agentIdentity.js";
import { getChatCreds } from "./chatCreds.js";
import { withScopeGuard } from "./scoutScope.js";
import { startSubAgent } from "./subAgent.js";

const SEV = ["critical", "high", "medium", "low"];
const NUM = new Intl.NumberFormat("en-US");
const TOP_PEOPLE = 12;
const TOP_GROUPS = 8;
const TOP_MODELS = 8;
// The traces' share of the facts, in characters (about 22,000 tokens), and how
// many of a person's traces that did nothing past chat are listed.
const TRACE_BUDGET = 80_000;
const CHATS_PER_PERSON = 20;

const clip = (s, n) => {
  const t = String(s ?? "").replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n - 3).trimEnd()}...` : t;
};

/** The name an agent goes by: its provider's product name, else the tag. */
export function agentName(tag) {
  const t = String(tag || "").trim();
  return PROVIDER_TAG_TO_IDENTITY[t.toLowerCase()]?.name || t || "Unknown";
}

const round2 = (n) => Math.round(n * 100) / 100;

/** "1 min", "43 min", "2.1 hr", "4.6 days": from a trace's first request to its last in the range. */
export function durationText(startMs, lastMs) {
  if (!Number.isFinite(startMs) || !Number.isFinite(lastMs)) return "";
  const min = Math.max(0, lastMs - startMs) / 60_000;
  if (min < 59.5) return `${Math.max(1, Math.round(min))} min`;
  const hr = min / 60;
  if (hr < 23.95) return `${String(Math.round(hr * 10) / 10)} hr`;
  return `${(min / 1440).toFixed(1)} days`;
}

// "$4,899+": whole dollars from $100 up, cents below, "+" where it is a floor.
const moneyText = (usd, floor = false) => `${usd >= 100 ? `$${NUM.format(Math.round(usd))}` : `$${usd.toFixed(2)}`}${floor ? "+" : ""}`;

// What review found on a trace, worst first, as the facts say it: its
// findings' titles where read ("echo" where the probe says the trace only read
// about the conduct), else its counts by severity.
function foundLines(found = [], bySev = {}) {
  if (!found.length) return SEV.filter((s) => bySev?.[s]).map((s) => `${bySev[s]} ${s}`);
  const rank = (s) => (SEV.includes(s) ? SEV.indexOf(s) : SEV.length);
  const worst = [...found].sort((a, b) => rank(a.severity) - rank(b.severity) || b.n - a.n);
  const lines = worst.slice(0, 3).map((f) => `${SEV.includes(f.severity) ? f.severity : "no severity"}: ${clip(f.title, 110)}${f.n > 1 ? ` (x${f.n})` : ""}${f.echo >= 0.5 ? " (echo)" : ""}`);
  return worst.length > 3 ? [...lines, `and ${worst.length - 3} more`] : lines;
}

/**
 * The facts a scope's analysis is written from (pure).
 *   rows     the scope's traces as the page lists them, newest first (lib/sessionFacts.js)
 *   titles   id -> the trace's title (its newest prompt's summary), or null
 *   costs    id -> { usd, measured } (lib/traceCosts.js), null where unread
 *   digests  lowercased id -> what the trace did (getTraceDigests), where read
 *   total    how many traces the scope holds, where counted
 *   query    the search the scope is, as typed ("" for every trace)
 *   range    the range, as the page names it
 *   capped   whether the rows are only the newest of the scope's traces
 * The traces are listed under the person who ran each, newest first, by a short
 * ref (`refs` maps it to the trace's id and is not sent). A trace that drew a
 * finding, touched a sensitive file or used a tool comes with what it did;
 * the rest are their titles, identical ones folded into one (`repeats`), at
 * most CHATS_PER_PERSON a person. What does not fit TRACE_BUDGET, routine
 * traces first, is counted as the person's `more`.
 */
export function buildFacts({ rows = [], titles = new Map(), costs = new Map(), digests = {}, total = null, query = "", range = "", capped = false }) {
  const costOf = (id) => costs.get(id) || null;
  let usd = 0, priced = 0, partial = false;
  const findings = { critical: 0, high: 0, medium: 0, low: 0 };
  let flaggedTraces = 0;
  const people = new Map(), groups = new Map(), agents = new Map(), models = new Map();
  const bump = (map, key, init) => {
    if (!key) return null;
    if (!map.has(key)) map.set(key, { ...init });
    return map.get(key);
  };
  for (const r of rows) {
    const c = costOf(r.id);
    if (c?.usd != null) { usd += c.usd; priced += 1; if (!c.measured) partial = true; } else partial = true;
    const sevs = r.bySev || {};
    if (Object.values(sevs).some(Boolean)) flaggedTraces += 1;
    for (const s of SEV) findings[s] += sevs[s] || 0;
    const p = bump(people, r.person, { traces: 0, usd: 0, partial: false, high: 0, group: r.group || "" });
    if (p) {
      p.traces += 1;
      if (c?.usd != null) { p.usd += c.usd; if (!c.measured) p.partial = true; } else p.partial = true;
      p.high += (sevs.critical || 0) + (sevs.high || 0);
    }
    const g = bump(groups, r.group, { traces: 0 });
    if (g) g.traces += 1;
    const a = bump(agents, agentName(r.agent), { traces: 0, usd: 0 });
    if (a) { a.traces += 1; a.usd += c?.usd || 0; }
    const m = bump(models, r.model, { traces: 0 });
    if (m) m.traces += 1;
  }
  const n = rows.length;
  const share = (k) => (n ? round2(k / n) : 0);

  // Each trace as the analysis reads it: in brief, and with what it did. Rank
  // 0 drew a finding or touched a sensitive file, 1 used a tool, 2 neither.
  const read = rows.map((r, i) => {
    const d = digests?.[String(r.id).toLowerCase()] || null;
    const c = costOf(r.id);
    const title = titles.get(r.id) || null;
    const brief = {
      title,
      agent: agentName(r.agent),
      model: r.model || undefined,
      duration: durationText(r.startMs, r.lastMs) || undefined,
      cost: c?.usd != null ? moneyText(c.usd, !c.measured) : undefined,
    };
    const asked = [...new Set((d?.asked || []).map((s) => clip(s, 160)))].filter((s) => s && s !== clip(title, 160)).slice(0, 2);
    const tools = [...(d?.tools || [])].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, k]) => `${name} ${k}`).join(", ");
    const paths = [...new Set(d?.paths || [])].slice(0, 3).map((p) => clip(p, 90));
    const found = foundLines(d?.found, r.bySev);
    const full = {
      ...brief,
      asked: asked.length ? asked : undefined,
      tools: tools || undefined,
      paths: paths.length ? paths : undefined,
      found: found.length ? found : undefined,
    };
    return { r, i, who: r.person || "", rank: found.length || paths.length ? 0 : tools ? 1 : 2, brief, full };
  });

  // Fill the budget, the traces that did most first, newest first within.
  const kept = new Map();
  const folds = new Map();
  const chats = new Map();
  const left = new Map();
  const leave = (who) => left.set(who, (left.get(who) || 0) + 1);
  let room = TRACE_BUDGET;
  for (const t of [...read].sort((a, b) => a.rank - b.rank || a.i - b.i)) {
    const same = `${t.who}|${t.brief.agent}|${t.brief.title}`;
    if (t.rank === 2 && t.brief.title && folds.has(same)) {
      const into = folds.get(same);
      into.repeats = (into.repeats || 1) + 1;
      continue;
    }
    if (t.rank === 2 && (chats.get(t.who) || 0) >= CHATS_PER_PERSON) { leave(t.who); continue; }
    let entry = t.rank === 2 ? t.brief : t.full;
    if (JSON.stringify(entry).length > room) entry = t.brief;
    const size = JSON.stringify(entry).length + 16;
    if (size > room) { leave(t.who); continue; }
    room -= size;
    kept.set(t.i, entry);
    if (t.rank === 2) {
      chats.set(t.who, (chats.get(t.who) || 0) + 1);
      if (t.brief.title) folds.set(same, entry);
    }
  }
  const refs = {};
  const byPerson = new Map();
  for (const t of read) {
    if (!byPerson.has(t.who)) byPerson.set(t.who, { person: t.who || null, group: t.r.group || undefined, traces: [] });
    const entry = kept.get(t.i);
    if (!entry) continue;
    const ref = `t${Object.keys(refs).length + 1}`;
    refs[ref] = String(t.r.id).toLowerCase();
    byPerson.get(t.who).traces.push({ ref, ...entry });
  }
  for (const [who, k] of left) byPerson.get(who).more = k;

  const top = (map, k) => [...map.entries()].sort((a, b) => b[1].traces - a[1].traces).slice(0, k);
  return {
    scope: { search: query || "every trace", range, traces: total ?? (capped ? null : n), read: n, newestOnly: !!capped },
    cost: { total: moneyText(usd, partial), tracesPriced: priced },
    findings: { ...findings, traces: flaggedTraces },
    people: top(people, TOP_PEOPLE).map(([name, p]) => ({ name, group: p.group || null, traces: p.traces, share: share(p.traces), cost: p.usd ? moneyText(p.usd, p.partial) : null, highOrCritical: p.high })),
    peopleCount: people.size,
    groups: top(groups, TOP_GROUPS).map(([name, g]) => ({ name, traces: g.traces, share: share(g.traces) })),
    agents: top(agents, 10).map(([name, a]) => ({ name, traces: a.traces, share: share(a.traces), cost: a.usd ? moneyText(a.usd) : null })),
    models: top(models, TOP_MODELS).map(([name, m]) => ({ name, traces: m.traces })),
    traces: [...byPerson.values()].filter((p) => p.traces.length || p.more),
    refs,
  };
}

export const ANALYSIS_SYSTEM_PROMPT = `You review a scope of AI agent traces for Origin's Explore page, as a trace reviewer would, and write what you find for a security or AI-governance lead. A trace is one conversation an agent ran for a person. The page beside you already lists every trace with its time, person, agent, duration, cost and findings, so do not restate the list. Do the reading the lead has no time for: what the people in this scope are doing with their agents, and which traces deserve a person opening them.

The facts (JSON) hold the scope, its people, agents, models and findings, and its traces grouped by the person who ran them, newest first. Each trace has a ref and a title (its newest prompt's summary). Where the data has them it also has: asked (other prompts in it), tools (each tool with how many of its prompts used it), paths (files it touched that hold keys or credentials), found (what review flagged, worst first; "echo" marks conduct the trace only read about, such as a pasted log or another trace under review), duration, cost, and repeats (how many identical traces it stands for). A person's "more" counts their traces left out for room; those that drew findings or touched sensitive files are left out last.

Write one reply that starts with its first sentence, with nothing before it:
1. One or two sentences, under 50 words, on what is happening in the scope: the work, and who is doing it, in plain and specific words ("Ada Park is moving the billing service's tests to Rust with Claude"). Name any mass of routine or automated work (a job that repeats, warmups, health checks) so the reader can set it aside.
2. The traces worth a look, most deserving first: at most four, one short paragraph each. Start with the trace's link, say what happened in it (what was asked, what the agent did and touched, what review found), why that deserves a look, and what to check on opening it. A paragraph may also link one closely related trace of the same person's. Name fewer when fewer stand out; if nothing does, say so in one sentence. Look for what a careful reviewer would want to see: risky actions (shell commands, credentials or secrets, production systems, pushing or deploying code), sensitive data, requests to get around controls, findings that the trace's own prompts and tools back up, an agent or model unusual for that person, a run far longer or costlier than the rest, work unlike the rest of that person's. An echo finding alone is weak: say the trace read about the conduct.
3. Optionally, one fenced block opened with \`\`\`filters: up to three lines, each "Label | clause", the filters that would sharpen this scope next, such as setting aside the routine mass. Each label is "Filter for" or "Filter out" and the value it filters by ("Filter out Ada Park", "Filter for Security").

Keep the whole reply under 250 words. Write only what the facts show: never invent a trace, person, tool, file or value, and say an inference as one ("looks like a test of the review rules"). Use a number only where it says something the list does not show at a glance ("71 of the newest 1,000 traces are the same nightly sync"). Costs and durations are display strings: write them as given, keeping a trailing "+". Never mention the facts, refs or how they are arranged. Keep sentences short. No headings, no lists, no closing summary.

Links:
- A person, group, agent or model you name is a link that filters by it: [Ada Park](filter:identity:"Ada Park"), [Security](filter:group:"Security"), [Claude](filter:agent:"Claude"), [opus-4.8](filter:model:"opus-4.8"). Link each name the first time you write it in a paragraph.
- A trace you name is a link by its ref, with a few words saying what it is: [Moving the decoder tests to Rust](trace:t12). Link only refs in the facts.
- Clauses are the search's own: identity:"<name>", group:"<group>", agent:"<agent>", model:"<model>", and a leading "-" to filter out (-identity:"<name>"). Several clauses in one filter are separated by spaces.

Words: say "traces", never "sessions". Say "cost", "duration", "filter for", "filter out". Refer to a person by name, never as he, she, his or her. Write in ASCII: no em dashes, no middle dots, no emoji.`;

const FOLLOW_UP_PROMPT = `${ANALYSIS_SYSTEM_PROMPT}

Now the reader asks a question about the same scope. Answer it in the same voice and with the same links, in a few short paragraphs, and end with a filters block only where a filter would help. Where a split answers the question (who or what holds most of the traces or the cost), you may open with one fenced block opened with \`\`\`breakdown holding JSON: {"title": "Traces by person" (or by group, agent or model), "total": "<the traces it splits>", "rows": [up to 3 objects {"label", "value", "share" (0 to 1), "filter" (a clause)}], "rest": {"label": "<n> more people", "value", "share"}}, its values display strings ("1,000", "$2,450+"). You may query the scope with the analytics tools to answer what the facts do not hold: call analytics_describe_schema before your first query, keep every query to the scope's traces, and use a grouped aggregate rather than reading rows.`;

/**
 * The task an analysis is given: the facts, and for a follow-up what was
 * written before and the question. `again` asks once more after a reply that
 * held only a note, not the analysis.
 */
export function analysisTask(facts, { question = null, earlier = "", again = false } = {}) {
  const shown = { ...facts };
  delete shown.refs;
  return [
    "FACTS (JSON):",
    JSON.stringify(shown),
    question && earlier ? `\nWHAT YOU WROTE BEFORE:\n${earlier}` : "",
    question ? `\nQUESTION:\n${question}` : "\nWrite the analysis.",
    again && !question ? "Your last reply was a note that you were starting, not the analysis. Write the whole analysis now." : "",
  ].filter(Boolean).join("\n");
}

const FENCE = /```(breakdown|filters)[^\n]*\n([\s\S]*?)(```|$)/g;
const TRACE_LINK = /\]\(trace:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)/gi;
const REF_LINK = /\[([^\]]*)\]\(trace:(t\d+)\)/g;

/**
 * An answer with each trace it links by ref (trace:t12) linked by the trace's
 * id instead, from the facts' `refs`; a ref the facts do not hold is left as
 * its words.
 */
export function resolveTraceRefs(text, refs = {}) {
  return String(text || "").replace(REF_LINK, (_, words, ref) => (refs?.[ref] ? `[${words}](trace:${refs[ref]})` : words));
}

/**
 * An answer, as far as it has streamed, in its parts: the prose to render, the
 * breakdown once its block has closed and parses, and the filters it ends with.
 * A block still open is held back rather than shown half-written.
 */
export function splitAnalysis(text) {
  let breakdown = null;
  const filters = [];
  const prose = String(text || "").replace(FENCE, (_, kind, body, close) => {
    if (!close) return "";
    if (kind === "breakdown") {
      try {
        const v = JSON.parse(body);
        if (v && Array.isArray(v.rows)) breakdown = v;
      } catch { /* a malformed block shows nothing rather than its JSON */ }
      return "\n\n";
    }
    for (const line of body.split("\n")) {
      const at = line.indexOf("|");
      if (at < 0) continue;
      const label = line.slice(0, at).trim();
      const clause = line.slice(at + 1).split("|")[0].trim();
      if (label && clause) filters.push({ label, clause });
    }
    return "\n\n";
  });
  return { prose: prose.replace(/\n{3,}/g, "\n\n").trim(), breakdown, filters: filters.slice(0, 3) };
}

/** The traces an answer links, in the order it first names them (ids lowercased). */
export function traceRefs(text) {
  const out = [];
  for (const m of String(text || "").matchAll(TRACE_LINK)) {
    const id = m[1].toLowerCase();
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

// The models that write the analysis, by the provider the workspace's chat runs
// on: the one that reviews a scope's traces best, then the one that writes it
// when that one stops (Opus 5.5 stops with content_filter on some scopes'
// security content; Sonnet 5.5 does not). A provider with no entry, or no
// second model, runs the workspace's own chat model.
const ANALYSIS_MODELS = {
  openrouter: ["anthropic/claude-opus-5.5", "anthropic/claude-sonnet-5.5"],
  anthropic: ["claude-opus-5-5", "claude-sonnet-5-5"],
  openai: ["gpt-6.1-sol"],
};

/**
 * Start writing an analysis (or, with `question`, answering one) and stream it
 * through `onEvent` (the agent's events), on the analysis model for the
 * workspace's provider (`fallback`: the second). The first analysis reads only
 * the facts, so it answers in one model call and thinks briefly: at a model's
 * default (high) depth it took a minute before its first word. A question may
 * also query the scope, with every query checked against its traces
 * (`scopePath`), at the model's default depth.
 * -> the sub-agent's handle: { child, abort, promise }.
 */
export async function startExploreAnalysis({ facts, question = null, earlier = "", again = false, fallback = false, scopePath = [], signal, onEvent }) {
  const getNamespaceKey = () => "explore-analysis";
  const tools = question
    ? [
      analyticsSchemaTool,
      withScopeGuard(createAnalyticsQueryTool({ getNamespaceKey }), { scopePath }),
      createExpandPreviewTool({ getNamespaceKey }),
    ]
    : [];
  const provider = await getChatCreds().then((creds) => creds.provider, () => "");
  return startSubAgent({
    role: "primary",
    model: ANALYSIS_MODELS[provider]?.[fallback ? 1 : 0] ?? null,
    signal,
    onEvent,
    tools,
    returnTool: false,
    thinkingLevel: question ? null : "low",
    systemPrompt: question ? FOLLOW_UP_PROMPT : ANALYSIS_SYSTEM_PROMPT,
    task: analysisTask(facts, { question, earlier, again }),
  });
}
